import { useState, useEffect, useCallback } from 'react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { User, AlertCircle, Image } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { motion } from 'framer-motion';
import ProfileImageSelector from './profile-image-selector';
import { fetchInstagramPosts } from '@/lib/instagram-post-service';
import { useToast } from '@/hooks/use-toast';

interface ChatProfileImageProps {
  username: string;
  src?: string;
  fallbackSrc?: string;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  onImageChange?: (newImageUrl: string) => void;
  showImageIcon?: boolean;
}

export default function ChatProfileImage({
  username,
  src,
  fallbackSrc = '/default-profile.png',
  size = 'md',
  className = '',
  onImageChange,
  showImageIcon = true
}: ChatProfileImageProps) {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [imageError, setImageError] = useState<boolean>(false);
  const [imageSrc, setImageSrc] = useState<string | undefined>(src);
  const [isHovered, setIsHovered] = useState<boolean>(false);
  const [selectorOpen, setSelectorOpen] = useState<boolean>(false);
  const [hasPosts, setHasPosts] = useState<boolean>(false);
  const [checkingPosts, setCheckingPosts] = useState<boolean>(false);
  const { toast } = useToast();

  const fetchProfileImage = useCallback(async (): Promise<void> => {
    if (!username) return;

    try {
      await new Promise(resolve => setTimeout(resolve, 500));

      if (!src) {
        const randomId = Math.floor(Math.random() * 1000);
        const randomImage = `https://picsum.photos/seed/${username || randomId}/200`;
        setImageSrc(randomImage);
      }
    } catch (error) {
      console.error('프로필 이미지 로드 오류:', error);
      setImageError(true);
    } finally {
      setIsLoading(false);
    }
  }, [username, src]);

  useEffect(() => {
    fetchProfileImage();
  }, [fetchProfileImage]);

  // 사용자 게시물 확인 (한 번만 실행)
  useEffect(() => {
    if (username) {
      setCheckingPosts(true);
      fetchInstagramPosts(username)
        .then(posts => {
          setHasPosts(posts.length > 0);
        })
        .catch(error => {
          console.error('게시물 확인 오류:', error);
          setHasPosts(false);
        })
        .finally(() => {
          setCheckingPosts(false);
        });
    }
  }, [username]);

  const handleOpenSelector = () => {
    if (!hasPosts) {
      // 게시물이 없으면 메시지만 표시하고 선택기를 열지 않음
      toast({
        title: "사용 가능한 게시물 없음",
        description: "이 계정에 사용 가능한 게시물이 없습니다.",
        variant: "destructive",
      });
      return;
    }
    setSelectorOpen(true);
  };

  const handleImageSelect = (newImageUrl: string) => {
    setImageSrc(newImageUrl);
    setImageError(false);

    // 상위 컴포넌트에 변경 알림
    if (onImageChange) {
      onImageChange(newImageUrl);
    }

    toast({
      title: "프로필 이미지 변경됨",
      description: "프로필 이미지가 성공적으로 변경되었습니다.",
    });
  };

  const sizeClasses = {
    sm: 'h-12 w-12',
    md: 'h-16 w-16',
    lg: 'h-20 w-20'
  };

  const finalImageSrc = imageError ? fallbackSrc : imageSrc;

  return (
    <>
      <motion.div
        className="relative group"
        onHoverStart={() => setIsHovered(true)}
        onHoverEnd={() => setIsHovered(false)}
        whileHover={{ scale: 1.05 }}
        transition={{ duration: 0.2 }}
        onClick={showImageIcon && hasPosts ? handleOpenSelector : undefined}
        style={{ cursor: showImageIcon && hasPosts ? 'pointer' : 'default' }}
      >
        {isLoading || checkingPosts ? (
          <Skeleton className={`${sizeClasses[size]} rounded-full ${className}`} />
        ) : (
          <Avatar className={`${sizeClasses[size]} ring-2 ring-primary/10 transition-all duration-300 ${isHovered && hasPosts && showImageIcon ? 'ring-primary/30 shadow-lg' : ''} ${className}`}>
            <AvatarImage
              src={finalImageSrc}
              alt={username}
              onError={() => setImageError(true)}
              className="object-cover"
              loading="lazy"
            />
            <AvatarFallback className="bg-gradient-to-br from-primary to-accent text-white">
              {imageError ? (
                <AlertCircle className="h-4 w-4" />
              ) : (
                <User className="h-4 w-4" />
              )}
            </AvatarFallback>
          </Avatar>
        )}

        {imageError && (
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-orange-500 flex items-center justify-center"
          >
            <AlertCircle className="h-2.5 w-2.5 text-white" />
          </motion.div>
        )}

        {hasPosts && showImageIcon && (
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{
              opacity: 1,
              scale: 1
            }}
            whileHover={{ scale: 1.1 }}
            transition={{ duration: 0.2 }}
            className="absolute -bottom-1 -left-1 h-5 w-5 rounded-full bg-primary/80 text-primary-foreground shadow-md flex items-center justify-center"
          >
            <Image className="h-3 w-3 text-white" />
          </motion.div>
        )}
      </motion.div>

      <ProfileImageSelector
        open={selectorOpen}
        username={username}
        onClose={() => setSelectorOpen(false)}
        onSelect={handleImageSelect}
      />
    </>
  );
}